// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Host, Element, Prop, State, h, Build } from '@stencil/core';

import { FormattedPromotion } from '../../types';
import { getPromotionEvent, debouncedLogEvent } from '../../utils/analytics';

import { fireImpressions } from '../../utils/impressions';
import { registerElToObserverList } from '../../utils/observer';
import { default as FeaturedPromoCard } from '../functional/FeaturedPromotionCard';
import { default as PromoCard } from '../functional/PromotionCard';

import type { PromotionOptions, ComponentType } from '../../utils/analytics';

@Component({
    tag: 'promotion-card',
    styleUrl: 'promotion-card.css',
    shadow: true,
})
export class PromotionCard {
    private defaultCallback() {
        return null;
    }

    @Element() el: HTMLElement;
    // props
    @Prop() isFeatured = false;
    @Prop() promotion: FormattedPromotion | null = null;
    @Prop() propertyId: number;
    @Prop() useBoxShadow = false;
    @Prop() eventOptions: { componentType: ComponentType; options: PromotionOptions };
    @Prop() darkMode = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';
    @Prop() promoCodeLabel: string;
    @Prop() noCodeLabel: string;
    @Prop() claimLabel: string;
    @Prop() seeMoreLabel: string;
    @Prop() positionString: string = '';
    @Prop() showValues = false;
    @Prop() showReviewLink = false;
    @Prop() callback: () => void = this.defaultCallback;

    // state
    @State() bonusCodeWasClicked = false;
    @State() termsToggle = false;

    onView() {
        if (!Build.isTesting && !Build.isServer) {
            this.logPromotionEvent('Impression');
            fireImpressions(
                this.promotion.impressionUrls,
                this.promotion.affiliateId,
                this.promotion.impressionTtl,
                this.promotion.impressionThrottle,
                this.promotion.impressionThrottleClick
            );
        }
    }

    connectedCallback() {
        if (!Build.isTesting && !Build.isServer) {
            registerElToObserverList(this.el, this.onView.bind(this));
        } else {
            this.onView();
        }
    }

    handleBonusCodeClick() {
        this.bonusCodeWasClicked = true;

        setTimeout(() => {
            this.bonusCodeWasClicked = false;
        }, 2500);
    }

    logPromotionEvent(action: 'Impression' | 'Click') {
        if (action === 'Click') {
            this.callback();
        }
        const i13n = getPromotionEvent({
            ...this.eventOptions,
            action,
            promotion: this.promotion,
        });
        debouncedLogEvent(this.propertyId, i13n);
    }

    handleTermsClick() {
        this.termsToggle = !this.termsToggle;
    }

    render() {
        return (
            <Host>
                <div class={`${this.darkMode ? 'dark-mode' : ''}`}>
                    {this.isFeatured ? (
                        <FeaturedPromoCard
                            promotion={this.promotion}
                            logPromotionClick={this.logPromotionEvent.bind(this, 'Click')}
                            handleBonusCodeClick={this.handleBonusCodeClick.bind(this)}
                            bonusCodeWasClicked={this.bonusCodeWasClicked}
                            promoCodeLabel={this.promoCodeLabel}
                            noCodeLabel={this.noCodeLabel}
                            claimLabel={this.claimLabel}
                            seeMoreLabel={this.seeMoreLabel}
                            terms={this.terms}
                        />
                    ) : (
                        <PromoCard
                            promotion={this.promotion}
                            logPromotionClick={this.logPromotionEvent.bind(this, 'Click')}
                            handleBonusCodeClick={this.handleBonusCodeClick.bind(this)}
                            bonusCodeWasClicked={this.bonusCodeWasClicked}
                            promoCodeLabel={this.promoCodeLabel}
                            noCodeLabel={this.noCodeLabel}
                            claimLabel={this.claimLabel}
                            seeMoreLabel={this.seeMoreLabel}
                            useBoxShadow={this.useBoxShadow}
                            terms={this.terms}
                            termsToggle={this.termsToggle}
                            handleTermsClick={this.handleTermsClick.bind(this)}
                            positionString={this.positionString}
                            showValues={this.showValues}
                            showReviewLink={this.showReviewLink}
                        />
                    )}
                </div>
            </Host>
        );
    }
}
